import { useCallback, useEffect, useRef } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

interface InfiniteScrollFooterProps {
  /** Attach to the sentinel: the page's own IntersectionObserver callback
   *  ref. Omit it and pass `onLoadMore` to let this component observe. */
  sentinelRef?: (node: HTMLDivElement) => void;
  /** Called when the sentinel scrolls into view and more can be loaded. */
  onLoadMore?: () => void;
  isLoading?: boolean;
  hasMore: boolean;
  /** Rows currently shown; the end-of-list line only appears once there are
   *  some (an empty list shows the page's own empty state instead). */
  count: number;
  /** Scroll container to observe within — a modal's scrolling body. Defaults
   *  to the viewport. */
  root?: Element | null;
  className?: string;
}

/**
 * The bottom of an infinitely scrolling list: an invisible trigger that loads
 * the next page as it comes into view, a spinner while that page loads, and a
 * quiet "end of list" line once everything is in.
 *
 * Replaces Prev / Next paging. Works either with a page's existing observer
 * (`sentinelRef`) or on its own (`onLoadMore`), which is what the modals and
 * panels use.
 */
export function InfiniteScrollFooter({
  sentinelRef,
  onLoadMore,
  isLoading = false,
  hasMore,
  count,
  root = null,
  className,
}: InfiniteScrollFooterProps) {
  const { t } = useTranslation("common");
  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef(onLoadMore);
  useEffect(() => {
    loadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  const canLoad = !!onLoadMore;
  const ownRef = useCallback(
    (node: HTMLDivElement | null) => {
      observer.current?.disconnect();
      if (!node || !canLoad || isLoading || !hasMore) return;
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) loadMoreRef.current?.();
        },
        // Start the next page a little before the end is actually reached.
        { root, rootMargin: "240px 0px" },
      );
      observer.current.observe(node);
    },
    // onLoadMore itself is read through a ref; only whether one exists matters.
    [canLoad, isLoading, hasMore, root],
  );

  useEffect(() => () => observer.current?.disconnect(), []);

  return (
    <div
      ref={(sentinelRef ?? ownRef) as React.Ref<HTMLDivElement>}
      className={clsx(
        "flex min-h-10 items-center justify-center py-3",
        className,
      )}
      aria-live="polite"
    >
      {isLoading && count > 0 ? (
        <span className="inline-flex items-center gap-2 rounded-full border border-[rgb(var(--border))] bg-[rgb(var(--card))] px-3 py-1.5 text-xs font-medium text-[rgb(var(--text-secondary))] shadow-sm">
          <Loader2
            size={13}
            className="animate-spin text-[rgb(var(--text-accent))]"
          />
          {t("infiniteScroll.loadingMore", { defaultValue: "Loading more…" })}
        </span>
      ) : !hasMore && count > 0 ? (
        <span className="inline-flex items-center gap-1.5 text-[11px] text-[rgb(var(--text-muted))]">
          <CheckCircle2 size={12} />
          {t("infiniteScroll.allLoaded", {
            defaultValue: "You've reached the end · {{count}} shown",
            count,
          })}
        </span>
      ) : null}
    </div>
  );
}
